import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Avatar from "../../components/common/Avatar";
import ClubVerifiedBadge from "../../components/agent-tender/ClubVerifiedBadge";
import FlagImg from "../../components/agent-tender/FlagImg";
import { BookmarkGlyph } from "../../components/agent-tender/glyphs";
import MessageAgentModal from "../../components/club-network/MessageAgentModal";
import { ChatBubbleGlyph } from "../../components/club-network/v2/cnGlyphs";
import FifaVerifiedBadge, {
  isFifaVerified,
} from "../../components/common/FifaVerifiedBadge";
import {
  ClubVerifiedPill,
  WindowPill,
  TrustCountPill,
} from "../../components/club-network/v2/cnPills";
import { usePermission } from "../../hooks/usePermission";
import { PERMISSIONS } from "../../config/permissions";
import { clubNetworkService } from "../../services/clubNetworkApi";
import {
  formatRequestTitle,
  formatTypeOfTender,
  formatLeagueTier,
  formatTransferWindow,
} from "../../constants/agentTenderChoices";
import { formatRange } from "../../utils/tenderFormatters";
import { buildFinRows } from "../../utils/agentRequestFigures";
import {
  formatAbsoluteDate,
  formatRelativeDate,
} from "../../utils/searchRequestFormat";

// =====================================================================
// N2-2 — Request detail, club side (Redesign 2.0 boards n2-2 / n2-2c +
// mobile board M2 @390px). Presentation layer only — the load effect,
// save toggle, openChat, back navigation and the MessageAgentModal wiring
// are byte-identical to the pre-rebuild page (handlers #45-#49, #2/#5/#6).
//
// Artboard grammar (N2-2, 560px white card):
//   back link "‹ Agent Requests" · badge row (Club Verified S1 pill +
//   window pill right) · title 22px/700 + meta row with dimmed U+00B7
//   separators (image flag + country · tier · bold fin figures · type) +
//   "Posted {relative}" · agent panel (#F8FAFC, person-first: 44px agent
//   avatar, name + Licensed pill, 16px agency mini-crest + agency line,
//   trust pill "N verified requests" right) · anonymity paragraph · one
//   primary CTA "Message agent" (r10, 14px, 13px padding).
// N2-2c (contacted): the CTA swaps to "Open chat" and the E8F1FF banner
//   with the S2 chat bubble (14×14) carries the existing attribution copy
//   ("Contacted by {member} · {date}", DoD 27 — graceful "Contacted").
//   Title-size drift between the two boards (22 vs 20px) resolved to the
//   N2-2 values per SPEC-EXTRACT §7.4.
// M2 (binding, 390px): inverted chrome — gray page, agent info in its own
//   white card, license as inline text, trust count inline in the sub-line,
//   meta as one 13px line, sticky bottom action bar with the full-width
//   CTA (r12/15px) in thumb reach. The "‹ Request" mobile header bar is
//   platform-shell chrome (Rule 5 / D-B4-1) — the back link renders at
//   every width instead.
//
// ANONYMITY (locked): this page renders posted_agency + responsible_agent
// only — the serializer carries no club field and nothing here resolves
// the club. Existing elements without artboard placement (bookmark save,
// age line, error state) stay wired best-fit (PARITY-B2.md, Rule 7).
// Trust presentation per Marin ruling D-MARIN-1 (2+3): ONLY the artboard
// "N verified requests" pill renders club-side — the fuller TrustStatsRow
// (aggregate posted/verified/revoked/deals) was removed from this
// club-facing surface (fraud = enforcement, not public counters). The
// per-request superseded/revoked WARN pill stays.
// =====================================================================

const ClubNetworkRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [messaging, setMessaging] = useState(false);
  const [saved, setSaved] = useState(false);
  // contact_agents is a ClubRole permission — without it the primary action is
  // replaced by an explanatory note (the backend refuses the contact anyway).
  const canContactAgents = usePermission(PERMISSIONS.CONTACT_AGENTS);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    clubNetworkService.getRequest(id).then((res) => {
      if (cancelled) return;
      if (res.success) {
        setRequest(res.data);
        setSaved(!!res.data.is_saved);
      } else setError(res.error || "Request not found.");
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Optimistic save/unsave (C12) — endpoints aren't idempotent, so we branch on
  // the known state and revert on failure.
  const onToggleSave = async () => {
    const was = saved;
    setSaved(!was);
    const res = was
      ? await clubNetworkService.unsaveRequest(id)
      : await clubNetworkService.saveRequest(id);
    if (!res.success) setSaved(was);
  };

  if (loading) {
    return <div className="flex justify-center py-20 text-gray-500">Loading…</div>;
  }
  if (error || !request) {
    return (
      <div className="flex flex-col items-center py-20 text-center gap-3">
        <p className="text-red-500">{error || "Request not found."}</p>
        <button
          onClick={() => navigate("/network/agent-requests")}
          className="px-4 py-2 border rounded-lg hover:bg-gray-50"
        >
          Back to Agent Requests
        </button>
      </div>
    );
  }

  const windowInfo = formatTransferWindow(request.transfer_window);
  const ageLimit = formatRange(request.min_age, request.max_age, "years");
  const agent = request.responsible_agent;
  const agency = request.posted_agency;
  const agentName = agent?.display_name || "the agent";
  const postedAgo = formatRelativeDate(request.created_on || request.created_at);
  const verifiedCount = request.trust_stats?.agent?.verified;
  const status = request.verification_status;

  // 16px agency mini-crest (§1.8) — logo when the agency has one, else its
  // initials on the white/bordered square.
  const agencyInitials = (agency?.name || "")
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const openChat = () => {
    if (request.chat_channel_id) navigate(`/inbox?channel=${request.chat_channel_id}`);
    else navigate("/inbox");
  };

  // The dimmed meta separator (N2-2: own #D1D5DB span; M2 renders the same
  // U+00B7 inline in the muted string color).
  const MetaDot = () => (
    <span className="text-[#D1D5DB] max-md:text-inherit" aria-hidden="true">
      ·
    </span>
  );

  return (
    <div className="bg-[#F8FAFC] min-h-screen">
      <div className="max-w-[560px] mx-auto px-4 pt-5 pb-24 md:px-0 md:py-8">
        <button
          onClick={() => navigate("/network/agent-requests")}
          className="text-[12.5px] font-semibold text-[#2283FF] mb-3 md:mb-4"
        >
          ‹ Agent Requests
        </button>

        {/* Desktop: the 560px white detail card. Mobile (M2): no card chrome —
            the content sits on the gray page, thumb-reach CTA in a bottom bar. */}
        <div className="flex flex-col gap-[14px] md:gap-[18px] md:bg-white md:border md:border-[#D6DBE3] md:rounded-xl md:shadow-[0_8px_24px_rgba(17,24,39,0.08)] md:pt-[26px] md:pb-[26px] md:pl-[30px] md:pr-[30px]">
          {/* Badge row — Club Verified (v3 S1 pill, locked tooltip) + window
              pill. Club side shows NOTHING for unverified (lead ruling D);
              superseded/revoked keep their existing warn pill (no artboard
              placement — OPEN). Bookmark save: existing function, best fit. */}
          <div className="flex items-center gap-2">
            {status === "club_verified" ? (
              <ClubVerifiedPill />
            ) : status === "superseded" || status === "revoked_fraud" ? (
              <ClubVerifiedBadge status={status} />
            ) : null}
            <div className="ml-auto flex items-center gap-2">
              {windowInfo && <WindowPill>{windowInfo.label || windowInfo.dates}</WindowPill>}
              <button
                type="button"
                onClick={onToggleSave}
                aria-label={saved ? "Unsave request" : "Save request"}
                aria-pressed={saved}
                title={saved ? "Saved" : "Save"}
                className={`w-[26px] h-[26px] inline-flex items-center justify-center rounded-lg border flex-none ${
                  saved
                    ? "border-[#BFDBFE] text-[#2283FF] bg-[#E8F1FF]"
                    : "border-[#E2E8F0] text-[#64748B] bg-white hover:bg-gray-50"
                }`}
              >
                <BookmarkGlyph saved={saved} />
              </button>
            </div>
          </div>

          {/* Title block — N2-2: 22px/700, meta 13.5px with dimmed dots;
              M2: 21px, meta one 13px line. */}
          <div className="flex flex-col gap-[6px] md:gap-2">
            <h1 className="text-[21px] md:text-[22px] font-bold text-[#111827] leading-[1.25] capitalize">
              {formatRequestTitle(request)}
            </h1>
            <div className="flex items-center gap-2 flex-wrap text-[13px] md:text-[13.5px] text-[#6B7280]">
              {request.country?.name && (
                <span className="inline-flex items-center gap-1.5">
                  <FlagImg
                    country={request.country}
                    className="w-[18px] h-[13px] rounded-[2.5px]"
                  />
                  {request.country.name}
                </span>
              )}
              {request.country?.name && <MetaDot />}
              <span>{formatLeagueTier(request.league_tier)}</span>
              {/* V2: real figures replace the budget band. */}
              {buildFinRows(request, "mo").map((r) => (
                <React.Fragment key={r.key}>
                  <MetaDot />
                  <span className="font-semibold text-[#111827]">
                    {r.label} {r.value}
                  </span>
                </React.Fragment>
              ))}
              <MetaDot />
              <span>{formatTypeOfTender(request.type_of_tender)}</span>
            </div>
            {postedAgo && (
              <span className="text-[11.5px] md:text-[12px] text-[#9CA3AF]">
                Posted {postedAgo}
              </span>
            )}
            {ageLimit && (
              <span className="text-[12px] text-[#9CA3AF]">Age {ageLimit}</span>
            )}
          </div>

          {/* Agent panel — person-first (Rule 9): the agent is the counterpart,
              the agency is secondary. Never the club. Desktop: #F8FAFC panel,
              44px avatar, FIFA Verified badge (D-A), mini-crest sub-line,
              trust pill right. M2: white card, 40px avatar, badge + trust
              count inline. */}
          <div className="bg-white border border-[#E5E7EB] rounded-xl pl-[15px] pr-[15px] py-[13px] flex items-center gap-[11px] md:bg-[#F8FAFC] md:pl-[18px] md:pr-[18px] md:py-4 md:gap-3">
            <span className="flex-none md:hidden">
              <Avatar src={agent?.avatar} fallbackText={agentName} size={40} />
            </span>
            <span className="flex-none hidden md:inline-flex">
              <Avatar src={agent?.avatar} fallbackText={agentName} size={44} />
            </span>
            <div className="flex flex-col gap-[2px] min-w-0 flex-1">
              <span className="flex items-center gap-[7px] min-w-0">
                <span className="text-[13px] md:text-[14px] font-semibold text-[#111827] truncate">
                  {agentName}
                </span>
                {/* D-A: the FIFA Verified badge replaced the "Licensed" pill
                    and the M2 inline text. Gate is isFifaVerified (Marin
                    2026-08-04: self-declaration carries the badge, only
                    failed_qr hides it); no badge shows nothing — never
                    "Unlicensed". */}
                {isFifaVerified(agent) && (
                  <>
                    {/* Wrapper spans carry the responsive visibility — the
                        badge's inline display would override the classes. */}
                    <span className="md:hidden inline-flex flex-none">
                      <FifaVerifiedBadge size="sm" />
                    </span>
                    <span className="hidden md:inline-flex flex-none">
                      <FifaVerifiedBadge size="md" />
                    </span>
                  </>
                )}
              </span>
              {agency?.name && (
                <span className="inline-flex items-center gap-[5px] text-[11.5px] md:text-[12px] text-[#6B7280] min-w-0">
                  <span className="hidden md:inline-flex w-4 h-4 rounded bg-white border border-[#E5E7EB] items-center justify-center text-[8px] font-bold text-[#6B7280] flex-none overflow-hidden">
                    {agency.logo_url ? (
                      <img
                        src={agency.logo_url}
                        alt=""
                        className="w-full h-full object-contain"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      agencyInitials
                    )}
                  </span>
                  <span className="truncate">
                    {agency.name}
                    {/* Agency country (artboard "· 🇩🇪 Germany") — the N2
                        serializer carries no agency country: renders nothing. */}
                    {/* M2: trust count rides inline in the sub-line. */}
                    {verifiedCount != null && (
                      <span className="md:hidden">
                        {" · "}
                        {verifiedCount}{" "}
                        {verifiedCount === 1
                          ? "verified request"
                          : "verified requests"}
                      </span>
                    )}
                  </span>
                  {agency.country && (
                    <FlagImg
                      country={agency.country}
                      className="hidden md:inline-block w-4 h-[11px] rounded-[2px]"
                    />
                  )}
                </span>
              )}
            </div>
            {verifiedCount != null && (
              <TrustCountPill className="ml-auto flex-none hidden md:inline-flex">
                {/* T029-2: "1 verified requests" was a plural error. The
                    codebase has no i18next plural forms anywhere (no
                    _one/_other suffixes, no t(..., {count})); the house
                    approach is a JS ternary over two whole corpus strings —
                    same as the sibling count line in ClubNetworkFeed.js. Both
                    phrases are kept whole so each stays one translatable text
                    node. */}
                {verifiedCount}{" "}
                {verifiedCount === 1 ? "verified request" : "verified requests"}
              </TrustCountPill>
            )}
          </div>

          {/* Anonymity paragraph — desktop N2-2 copy (pre-rebuild string kept
              for translation matching) / shorter M2 variant. */}
          <p className="hidden md:block text-[12.5px] text-[#6B7280] leading-[1.55]">
            The club behind this request is anonymous by design. {agentName} is your
            counterpart — introduce a player or ask about the search.
          </p>
          <p className="md:hidden text-[12px] text-[#6B7280] leading-[1.55]">
            The club behind this request is anonymous by design. {agentName} is your
            counterpart.
          </p>

          {/* N2-2c contacted banner — S2 bubble 14×14 + existing attribution
              (DoD 27), graceful "Contacted" when the API omits it. */}
          {request.you_contacted && (
            <div className="flex items-center gap-2 bg-[#E8F1FF] rounded-[10px] pl-3.5 pr-3.5 py-[11px]">
              <ChatBubbleGlyph size={14} className="flex-none" />
              <span className="text-[12.5px] font-semibold text-[#1B6FE0] min-w-0 truncate">
                {request.contacted_by?.name ? (
                  <>
                    Contacted by {request.contacted_by.name}
                    {request.contacted_at
                      ? ` · ${formatAbsoluteDate(request.contacted_at)}`
                      : ""}
                  </>
                ) : (
                  "Contacted"
                )}
              </span>
            </div>
          )}

          {/* One primary action (N2-2/N2-2c). Mobile: sticky bottom action bar
              (M2, thumb reach). Same handlers as before: #47 openChat / #49
              setMessaging; without contact_agents the note replaces the CTA. */}
          {request.you_contacted || canContactAgents ? (
            <div className="max-md:fixed max-md:bottom-0 max-md:left-0 max-md:right-0 max-md:z-20 max-md:bg-white max-md:border-t max-md:border-[#E5E7EB] max-md:px-4 max-md:pt-3 max-md:pb-4">
              <button
                onClick={request.you_contacted ? openChat : () => setMessaging(true)}
                className="w-full bg-[#2283FF] text-white font-semibold rounded-xl text-[15px] py-[15px] md:rounded-[10px] md:text-[14px] md:py-[13px] hover:bg-[#1B6FE0]"
              >
                {request.you_contacted ? "Open chat" : "Message agent"}
              </button>
            </div>
          ) : (
            <p className="text-[12.5px] text-[#6B7280] bg-white md:bg-[#F8FAFC] border border-[#E5E7EB] rounded-xl px-4 py-3 text-center">
              You don't have permission to contact agents. Ask your club admin.
            </p>
          )}
        </div>
      </div>

      {messaging && (
        <MessageAgentModal
          request={request}
          onClose={() => setMessaging(false)}
          onSent={() => {
            setMessaging(false);
            setRequest((prev) => ({ ...prev, you_contacted: true }));
          }}
        />
      )}
    </div>
  );
};

export default ClubNetworkRequestDetail;
